import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Row, Col } from 'react-bootstrap';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputTextarea } from 'primereact/inputtextarea';
import { Rating } from 'primereact/rating';
import CustomToast from '../../components/CustomToast';
import StatusChangeDialog from './StatusChangeDialog';
import {
  PERM,
  APPLICATION_STATUS,
  APPLICATION_HISTORY_ACTIONS,
  EMAIL_OUTCOME,
  apiErrorMessage,
  candidatePath,
  formatDate,
  formatDateTime,
  formatFileSize,
  openBlob,
  saveBlob,
  stars,
  useCareersPermissions,
  useCareersRequest,
} from './careersShared';
import {
  CareersPage, EmptyState, Loading, PageHeader, Panel, PersonAvatar, StatusBadge,
} from './careersUI';
import './careers.css';

const LIST_PATH = '/Graphe/dashboard/careers/applications';

/** "Github - https://github.com/x" → { label: 'Github', url } */
export function parseLink(line) {
  const url = line.match(/https?:\/\/\S+/i)?.[0];
  if (!url) return { label: line, url: null };
  const label = line.replace(url, '').replace(/[\s:–—-]+$/, '').trim();
  return { label: label || url, url };
}

function Detail({ label, wide = false, children }) {
  return (
    <div className={wide ? 'is-wide' : ''}>
      <dt>{label}</dt>
      <dd>{children || <span className="text-muted">—</span>}</dd>
    </div>
  );
}

function TimelineEntry({ row, assigneeNames }) {
  const statusLabel = (key) => APPLICATION_STATUS[key]?.label || key;
  const personLabel = (id) => (id ? assigneeNames[id] || `User #${id}` : 'Unassigned');
  let title = APPLICATION_HISTORY_ACTIONS[row.action] || row.action;
  let detail = null;

  if (row.action === 'status_changed') {
    title = row.from_status ? `${statusLabel(row.from_status)} → ${statusLabel(row.to_status)}` : statusLabel(row.to_status);
  } else if (row.action === 'rating') {
    detail = `${stars(row.meta?.from)} → ${stars(row.meta?.to)}`;
  } else if (row.action === 'assigned') {
    detail = `${personLabel(row.meta?.from)} → ${personLabel(row.meta?.to)}`;
  }

  return (
    <li>
      <div className="d-flex justify-content-between gap-2">
        <span className="careers-timeline-title">{title}</span>
        <span className="careers-timeline-when">{formatDateTime(row.createdAt)}</span>
      </div>
      <span className="careers-meta">by {row.actor_name}</span>
      {detail && <div className="small mt-1">{detail}</div>}
      {row.shown_to_candidate && row.action === 'status_changed' && (
        <div className="careers-candidate-note">
          <span className="careers-note-label">Candidate sees</span>
          {row.shown_to_candidate}
        </div>
      )}
      {row.internal_note && (
        <div className="careers-internal-note">
          <span className="careers-note-label">Internal note</span>
          {row.internal_note}
        </div>
      )}
      {row.meta?.email && <span className="careers-meta mt-1"><i className="pi pi-envelope me-1" aria-hidden="true" />{EMAIL_OUTCOME[row.meta.email] || row.meta.email}</span>}
    </li>
  );
}

const ApplicationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const request = useCareersRequest();
  const can = useCareersPermissions();
  const toastRef = useRef(null);

  const [application, setApplication] = useState(null);
  const [assignees, setAssignees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [statusOpen, setStatusOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const [fileBusy, setFileBusy] = useState(null);

  const canManage = can(PERM.manageApplications);
  const notify = (severity, summary, detail) => toastRef.current?.show(severity, summary, detail);

  const load = useCallback(async () => {
    setLoadError('');
    try {
      const data = await request(`/applications/${id}`);
      setApplication(data.application);
    } catch (error) {
      setLoadError(apiErrorMessage(error, 'Could not load this application.'));
    } finally {
      setLoading(false);
    }
  }, [id, request]);

  useEffect(() => { setLoading(true); load(); }, [load]);

  useEffect(() => {
    if (canManage) request('/assignees').then((data) => setAssignees(data.users || [])).catch(() => {});
  }, [request, canManage]);

  /** Run an update that returns { application }. */
  const update = async (action, successMessage) => {
    setBusy(true);
    try {
      const data = await action();
      setApplication(data.application);
      if (successMessage) notify('success', 'Saved', successMessage);
      return true;
    } catch (error) {
      notify('error', 'Not saved', apiErrorMessage(error));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const changeStatus = async (body) => {
    const ok = await update(
      () => request(`/applications/${id}/status`, { method: 'patch', data: body }),
      `Moved to ${APPLICATION_STATUS[body.status].label}.${body.notify_candidate ? ' The candidate will be emailed.' : ''}`,
    );
    if (ok) {
      setStatusOpen(false);
      // The email outcome is recorded a moment later
      if (body.notify_candidate) setTimeout(load, 3000);
    }
  };

  const setRating = (rating) => update(() => request(`/applications/${id}`, { method: 'patch', data: { rating } }));
  const setAssignee = (userId) => update(
    () => request(`/applications/${id}`, { method: 'patch', data: { assigned_to: userId ?? null } }),
    userId ? 'Application assigned.' : 'Application unassigned.',
  );

  const addNote = async () => {
    if (!note.trim()) return;
    const ok = await update(() => request(`/applications/${id}/notes`, { method: 'post', data: { internal_note: note.trim() } }), 'Note added.');
    if (ok) setNote('');
  };

  const getFile = async (file, preview) => {
    setFileBusy(file.file_id);
    try {
      const blob = await request(`/applications/${id}/files/${file.file_id}`, {
        params: preview ? { inline: 1 } : undefined,
        responseType: 'blob',
      });
      if (preview) openBlob(new Blob([blob], { type: file.mime_type }));
      else saveBlob(blob, file.original_name);
    } catch (error) {
      notify('error', 'Download failed', apiErrorMessage(error, 'Could not download the file.'));
    } finally {
      setFileBusy(null);
    }
  };

  const back = { label: 'Applications', onClick: () => navigate(LIST_PATH) };

  if (loading) {
    return <CareersPage><Loading /></CareersPage>;
  }
  if (loadError || !application) {
    return (
      <CareersPage>
        <PageHeader title="Application" back={back} />
        <EmptyState icon="pi-exclamation-circle" title={loadError || 'Application not found.'}>
          <Button label="Back to applications" text onClick={() => navigate(LIST_PATH)} />
        </EmptyState>
      </CareersPage>
    );
  }

  const { job, candidate } = application;
  const assigneeNames = Object.fromEntries(assignees.map((user) => [user.user_id, user.name]));
  if (application.assigned_to && application.assigned_to_name) assigneeNames[application.assigned_to] = application.assigned_to_name;

  return (
    <CareersPage>
      <CustomToast ref={toastRef} />
      <PageHeader title="Application" back={back} />

      <section className="careers-card">
        <div className="careers-profile">
          <PersonAvatar name={application.name} size="lg" />
          <div className="careers-profile-main">
            <h2 className="careers-profile-name">{application.name}</h2>
            <div className="careers-contact">
              <a href={`mailto:${application.email}`}><i className="pi pi-envelope" aria-hidden="true" />{application.email}</a>
              <a href={`tel:+91${application.phone}`}><i className="pi pi-phone" aria-hidden="true" />{application.phone}</a>
              {application.hometown && <span><i className="pi pi-map-marker" aria-hidden="true" />{application.hometown}</span>}
            </div>
          </div>
          <div className="d-flex align-items-center flex-wrap gap-2">
            <StatusBadge status={application.status} />
            {canManage && application.allowed_statuses.length > 0 && (
              <Button 
              label="Change status" 
              icon="pi pi-arrow-right-arrow-left" 
              severity="help" 
              onClick={() => setStatusOpen(true)}
              className="py-1"
              size="small"
              />
            )}
          </div>
        </div>
        <div className="careers-applied-for">
          <div>
            <span className="careers-overline">Applied for</span>
            <h3>{job?.title || 'Unknown job'}</h3>
            <span className="careers-meta">
              {job?.category}{job?.category ? ' · ' : ''}Applied {formatDateTime(application.createdAt)}
            </span>
          </div>
          {job && !job.is_public && (
            <span className="careers-warning-note align-self-start mt-0">
              <i className="pi pi-exclamation-triangle me-1" aria-hidden="true" />
              {job.is_deleted ? 'This job was deleted.' : 'This job is not currently on the portal.'}
            </span>
          )}
        </div>
      </section>

      <Row className="g-3">
        <Col lg={8}>
          <Panel title="Candidate details" icon="pi-user">
            <dl className="careers-detail-grid">
              <Detail label="Gender">{application.gender}</Detail>
              <Detail label="Current organization">{application.organization}</Detail>
              <Detail label="Current role">{application.job_role}</Detail>
              <Detail label="Total experience">{application.experience}</Detail>
              <Detail label="Expected CTC">{application.expected_ctc ? `₹${application.expected_ctc} LPA` : null}</Detail>
              <Detail label="Profile links" wide>
                {application.links.length > 0 && (
                  <ul className="list-unstyled mb-0">
                    {application.links.map((line) => {
                      const { label, url } = parseLink(line);
                      return (
                        <li key={line}>
                          {url ? <>{label !== url && `${label}: `}<a href={url} target="_blank" rel="noopener noreferrer nofollow">{url}</a></> : label}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </Detail>
              <Detail label="Strengths" wide>{application.strengths}</Detail>
              <Detail label="Weaknesses" wide>{application.weaknesses}</Detail>
            </dl>
          </Panel>

          <Panel title="CV & documents" icon="pi-paperclip">
            {application.files.length === 0 && <p className="text-muted mb-0">No files.</p>}
            {application.files.map((file) => {
              const isPdf = file.mime_type === 'application/pdf';
              return (
                <div className="careers-file-row" key={file.file_id}>
                  <span className={`careers-file-icon ${isPdf ? '' : 'is-doc'}`}>
                    <i className={`pi ${isPdf ? 'pi-file-pdf' : 'pi-file-word'}`} aria-hidden="true" />
                  </span>
                  <div className="flex-grow-1 text-truncate">
                    <span className="careers-primary-text">{file.original_name}</span>
                    <span className="careers-meta">{formatFileSize(file.size_bytes)}</span>
                  </div>
                  {isPdf && (
                    <Button
                      label="Open"
                      icon="pi pi-eye"
                      severity="secondary"
                      outlined
                      size="small"
                      className="py-1"
                      loading={fileBusy === file.file_id}
                      aria-label={`Open ${file.original_name}`}
                      onClick={() => getFile(file, true)}
                    />
                  )}
                  <Button
                    label="Download"
                    icon="pi pi-download"
                    severity="success"
                    outlined
                    size="small"
                    className="py-1"
                    disabled={fileBusy === file.file_id}
                    aria-label={`Download ${file.original_name}`}
                    onClick={() => getFile(file, false)}
                  />
                </div>
              );
            })}
          </Panel>

          <Panel title="Activity" icon="pi-history">
            <ol className="careers-timeline">
              {application.history.map((row) => (
                <TimelineEntry key={row.history_id} row={row} assigneeNames={assigneeNames} />
              ))}
            </ol>
          </Panel>
        </Col>

        <Col lg={4}>
          <div className="careers-sticky">
            {canManage ? (
              <Panel title="Review" icon="pi-star" className="careers-form">
                <div className="careers-side-section">
                  <label htmlFor="application-rating">Rating</label>
                  <Rating
                    id="application-rating"
                    value={application.rating || 0}
                    onChange={(e) => setRating(e.value || null)}
                    disabled={busy}
                  />
                </div>
                <div className="careers-side-section">
                  <label htmlFor="application-assignee">Assigned to</label>
                  <Dropdown
                    inputId="application-assignee"
                    value={application.assigned_to}
                    options={assignees.map((user) => ({ label: user.name, value: user.user_id }))}
                    onChange={(e) => setAssignee(e.value)}
                    placeholder="Unassigned"
                    showClear
                    filter={assignees.length > 8}
                    disabled={busy}
                  />
                </div>
                <div className="careers-side-section">
                  <label htmlFor="application-note">Add an internal note</label>
                  <InputTextarea
                    id="application-note"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    rows={3}
                    autoResize
                    maxLength={4000}
                    placeholder="Only the hiring team sees this"
                  />
                  <div className="text-end mt-2">
                    <Button label="Add note" icon="pi pi-comment" size="small" severity="help" outlined onClick={addNote} disabled={busy || !note.trim()} />
                  </div>
                </div>
              </Panel>
            ) : (
              <Panel title="Review" icon="pi-star">
                <p className="mb-1 careers-stars">{stars(application.rating)}</p>
                <small className="text-muted">Assigned to {application.assigned_to_name || 'nobody'}</small>
              </Panel>
            )}

            {candidate && (
              <Panel title="Candidate account" icon="pi-id-card">
                <dl className="careers-detail-list mb-0">
                  <dt>Email</dt>
                  <dd>
                    {candidate.email}
                    <span className="careers-meta">
                      {candidate.email_verified
                        ? <><i className="pi pi-verified text-success me-1" aria-hidden="true" />Verified</>
                        : 'Not verified'}
                    </span>
                  </dd>
                  <dt>Joined</dt>
                  <dd>{formatDate(candidate.joined_at)}</dd>
                  <dt>Other applications</dt>
                  <dd className="mb-0">
                    {candidate.other_applications || 'None'}
                    {!candidate.is_active && <span className="careers-badge is-off ms-2">Deactivated</span>}
                  </dd>
                </dl>
                {can(PERM.manageCandidates) && (
                  <Link to={candidatePath(candidate.candidate_id)} className="d-inline-block mt-3 small fw-semibold">View candidate</Link>
                )}
              </Panel>
            )}
          </div>
        </Col>
      </Row>

      <StatusChangeDialog
        visible={statusOpen}
        title={`Change status of ${application.name}`}
        statuses={application.allowed_statuses}
        busy={busy}
        onHide={() => setStatusOpen(false)}
        onSubmit={changeStatus}
      />
    </CareersPage>
  );
};

export default ApplicationDetail;
